const KORA_CONFIG_BASE = "https://konko.ai/kora-config";

export const FHIR_CODES = {
  EHR_CONNECTION: "https://konko.ai/fhir/code/ehr-connection",
  EHR_CONNECTION_TYPE: "https://konko.ai/fhir/CodeSystem/ehr-connection-type",
  MEMBER: "https://konko.ai/fhir/code/member",
  KORA_CONFIG: "kora-config",
  KORA_CONFIG_SYSTEM: `${KORA_CONFIG_BASE}/CodeSystem/resource`,
  OFFICE_VISIT: "office-visit",
} as const;

export const FHIR_IDENTIFIER_SYSTEMS = {
  /**
   * The `identifier[]` system carrying the clinic code name. Display output
   * only — never a lookup key.
   */
  KORA_CONFIG: KORA_CONFIG_BASE,
  /**
   * Set by AKB on every practitioner it writes. App members are
   * `Practitioner`s too, without it, so this system tells the two apart.
   */
  PRACTITIONER: "https://konko.ai/practitioner",
  /**
   * Agent Scheduling finds a practitioner only by this identifier. An admin
   * adds it by hand at onboarding. Its `DOC` values are numbered apart from
   * AKB's, so the same value in both systems can be two different
   * practitioners.
   */
  KNOWLEDGE_BASE_ID: "https://konko.ai/fhir/knowledge-base-id",
  PRACTITIONER_ROLE: "https://konko.ai/practitioner-role",
  LOCATION: "https://konko.ai/location",
  HEALTHCARE_SERVICE: "https://konko.ai/healthcare-service",
  /**
   * The clinic's own `Organization`, one per clinic, written by AKB from the
   * clinic details sheet. Its value is the clinic code name.
   */
  CLINIC_DETAILS: "https://konko.ai/clinic-details",
} as const;

export const FHIR_UCUM = {
  SYSTEM: "http://unitsofmeasure.org",
  SECONDS_CODE: "s",
} as const;

export const FHIR_EXTENSIONS = {
  SCHEDULING_PARAMETERS:
    "https://medplum.com/fhir/StructureDefinition/SchedulingParameters",
  EHR_CONNECTION_REF:
    "https://konko.ai/fhir/StructureDefinition/ehr-connection-ref",
  EHR_CONNECTION: "https://konko.ai/fhir/StructureDefinition/ehr-connection",
  TIMEZONE: "http://hl7.org/fhir/StructureDefinition/timezone",
  CLINIC_DETAILS_TAGLINE: "https://konko.ai/clinic-details/tagline",
  PRACTITIONER_BIOGRAPHY: "https://konko.ai/practitioner/biography",
  LOCATION_OPERATING_HOURS_NARRATIVE:
    "https://konko.ai/location/operating-hours-narrative",
  LOCATION_URL: "https://konko.ai/location/location-url",
  HEALTHCARE_SERVICE_PRICE_MINIMUM:
    "https://konko.ai/healthcare-service/price-minimum-narrative",
  HEALTHCARE_SERVICE_PRICE_MAXIMUM:
    "https://konko.ai/healthcare-service/price-maximum-narrative",
  HEALTHCARE_SERVICE_CURRENCY: "https://konko.ai/healthcare-service/currency",
  HEALTHCARE_SERVICE_RESULTS_LEAD_TIME:
    "https://konko.ai/healthcare-service/results-lead-time",
  HEALTHCARE_SERVICE_PREREQUISITES:
    "https://konko.ai/healthcare-service/service-prerequisites",
  HEALTHCARE_SERVICE_APPOINTMENT_TYPE_CODE:
    "https://konko.ai/healthcare-service/appointment-type-code",
  HEALTHCARE_SERVICE_ALL_LOCATIONS:
    "https://konko.ai/healthcare-service/all-locations",
  HEALTHCARE_SERVICE_AUTO_SCHEDULING_ALLOWED:
    "https://konko.ai/healthcare-service/auto-scheduling-allowed",
  MEMBER_ROLE: "https://konko.ai/fhir/StructureDefinition/member-role",
  MEMBER_USER: "https://konko.ai/fhir/StructureDefinition/member-user",
  MEMBER_PROFILE: "https://konko.ai/fhir/StructureDefinition/member-profile",
  MEMBER_PROJECT_MEMBERSHIP:
    "https://konko.ai/fhir/StructureDefinition/member-project-membership",
  KORA_CONFIG_IDENTITY: `${KORA_CONFIG_BASE}/identity`,
  KORA_CONFIG_TONE: `${KORA_CONFIG_BASE}/tone`,
  KORA_CONFIG_ESCALATION: `${KORA_CONFIG_BASE}/escalation`,
  KORA_CONFIG_FOLLOW_UP: `${KORA_CONFIG_BASE}/follow-up`,
  KORA_CONFIG_RECOMMENDED_SERVICES: `${KORA_CONFIG_BASE}/recommended-services`,
  KORA_CONFIG_SCHEDULING: `${KORA_CONFIG_BASE}/scheduling`,
  KORA_CONFIG_ESCALATION_CONFIGURATION: `${KORA_CONFIG_BASE}/escalation-configuration`,
} as const;

export const FHIR_EXTENSION_URLS = {
  SCHEDULING_PARAMETERS: {
    AVAILABILITY: "availability",
    DURATION: "duration",
    ALIGNMENT_INTERVAL: "alignmentInterval",
    BUFFER: "bufferAfter",
    BUFFER_LEGACY: "afterBuffer",
    SERVICE_TYPE: "serviceType",
  },
  EHR_CONNECTION_REF: {
    CONNECTION_ID: "connection_id",
  },
  EHR_CONNECTION: {
    STATUS: "status",
    TYPE: "type",
    ERROR: "error",
    MESSAGE: "message",
  },
  KORA_CONFIG_IDENTITY: {
    CODE_NAME: "code-name",
    AGENT_NAME: "agent-name",
    ROLE_DESCRIPTION: "role-description",
    GREETING: "greeting",
    ASK_NAME_IN_GREETING: "ask-name-in-greeting",
  },
  KORA_CONFIG_TONE: {
    FORMALITY_ADDRESS: "formality-address",
    PUNCTUATION_STYLE: "punctuation-style",
    EMOJIS_ALLOWED: "emojis-allowed",
    PREFERRED_EMOJIS: "preferred-emojis",
    PREFERRED_EMOJIS_DESCRIPTION: "preferred-emojis-description",
  },
  KORA_CONFIG_ESCALATION: {
    AUTO_RATE: "auto-rate",
    SESSION_LABEL: "session-label",
    PRIVATE_NOTE: "private-note",
  },
  KORA_CONFIG_FOLLOW_UP: {
    ID: "id",
    DELAY: "delay",
    GUIDELINES: "guidelines",
    SEND_WINDOW: "send-window",
  },
  /**
   * Sub-extensions of `KORA_CONFIG_FOLLOW_UP.SEND_WINDOW`, one level deeper
   * than every other group here — the resource's only depth-3 structure.
   */
  KORA_CONFIG_SEND_WINDOW: {
    WEEKDAY: "weekday",
    START_TIME: "start-time",
    END_TIME: "end-time",
  },
  KORA_CONFIG_RECOMMENDED_SERVICES: {
    NEW_PATIENT: "new-patient",
    EXISTING_PATIENT: "existing-patient",
  },
  KORA_CONFIG_SCHEDULING: {
    MAX_SLOTS_PER_MESSAGE: "max-slots-per-message",
    SCHEDULING_BUFFER: "scheduling-buffer",
    CANCELLATION_BUFFER: "cancellation-buffer",
    RESCHEDULING_BUFFER: "rescheduling-buffer",
  },
  KORA_CONFIG_ESCALATION_CONFIGURATION: {
    ESCALATOR_CLINIC_GUIDELINES: "escalator-clinic-guidelines",
    HUMAN_SCHEDULING_CLINIC_GUIDELINES: "human-scheduling-clinic-guidelines",
    SCHEDULING_LINK_ENABLED: "scheduling-link-enabled",
    SCHEDULING_PROVIDER: "scheduling-provider",
    SCHEDULING_LINK: "scheduling-link",
    OFFER_CALLBACK_AS_FALLBACK: "offer-callback-as-fallback",
    SCHEDULING_LINK_CLINIC_GUIDELINES: "scheduling-link-clinic-guidelines",
  },
} as const;
